import React, {type ReactNode, useEffect, useRef, useState} from "react";
import {ResponsiveGridLayout, useContainerWidth} from "react-grid-layout";
import type {ResponsiveLayouts} from "react-grid-layout";
import {HOME_DASHBOARD_BREAKPOINTS, HOME_DASHBOARD_COLUMNS, HOME_DASHBOARD_MARGIN, type HomeDashboardBreakpoint} from "./HomeDashboardLayout";

export const HOME_DASHBOARD_RESIZE_SETTLE_MS = 200;

const dashboardDragConfig = {enabled: false} as const;
const dashboardResizeConfig = {enabled: false} as const;
const dashboardWidthObserverStyle = {width: "100%", minWidth: 0} as const;

interface IHomeDashboardGridProps {
	readonly children?: ReactNode;
	readonly layouts: ResponsiveLayouts<HomeDashboardBreakpoint>;
}

interface IResponsiveHomeDashboardGridProps extends IHomeDashboardGridProps {
	readonly width: number;
}

const useSettledHomeDashboardWidth = (measuredWidth: number, mounted: boolean): number | null => {
	const [settledWidth, setSettledWidth] = useState<number | null>(null);
	const hasMeasured = useRef(false);

	useEffect(() => {
		if (!mounted) {
			return;
		}

		if (!hasMeasured.current) {
			hasMeasured.current = true;
			setSettledWidth(measuredWidth);
			return;
		}

		const timeoutId = window.setTimeout(() => {
			setSettledWidth((currentWidth) => (currentWidth === measuredWidth ? currentWidth : measuredWidth));
		}, HOME_DASHBOARD_RESIZE_SETTLE_MS);

		return () => window.clearTimeout(timeoutId);
	}, [measuredWidth, mounted]);

	// The first real measurement is safe to use immediately. Only subsequent
	// ResizeObserver updates need to wait for the resize gesture to settle.
	return settledWidth ?? (mounted ? measuredWidth : null);
};

const ResponsiveHomeDashboardGrid = React.memo(({children, layouts, width}: IResponsiveHomeDashboardGridProps) => {
	return (
		<ResponsiveGridLayout
			className="home-dashboard-grid"
			width={width}
			layouts={layouts}
			breakpoints={HOME_DASHBOARD_BREAKPOINTS}
			cols={HOME_DASHBOARD_COLUMNS}
			margin={HOME_DASHBOARD_MARGIN}
			dragConfig={dashboardDragConfig}
			resizeConfig={dashboardResizeConfig}
		>
			{children}
		</ResponsiveGridLayout>
	);
});

ResponsiveHomeDashboardGrid.displayName = "ResponsiveHomeDashboardGrid";

export const HomeDashboardGrid = ({children, layouts}: IHomeDashboardGridProps) => {
	const {width: measuredWidth, containerRef, mounted} = useContainerWidth({measureBeforeMount: true});
	const settledWidth = useSettledHomeDashboardWidth(measuredWidth, mounted);

	return (
		<div
			ref={containerRef}
			className="home-dashboard-grid-width-observer"
			style={dashboardWidthObserverStyle}
		>
			{settledWidth !== null ? (
				<ResponsiveHomeDashboardGrid
					width={settledWidth}
					layouts={layouts}
				>
					{children}
				</ResponsiveHomeDashboardGrid>
			) : null}
		</div>
	);
};
